import type { CompanyProfile, CreditNote } from "@/api/types";
import { useTaxWords } from "@/app/session";
import { formatAmount } from "@/lib/money";
import { PrintAmountInWords, PrintFooter, PrintHeader, PrintSignature } from "./DocumentSheet";

/**
 * A credit note.
 *
 * It names the invoice it credits and the reason, both on the face of it. A credit note that does
 * not say what it corrects cannot be reconciled by whoever receives it — and the reason is what a
 * tax authority asks about when revenue already reported goes down.
 */
export function CreditNotePrint({
  note,
  seller,
}: {
  note: CreditNote;
  seller: CompanyProfile | undefined;
}) {
  const say = useTaxWords();
  return (
    <article>
      <PrintHeader
        seller={seller}
        documentTitle={note.status === "draft" ? "Draft credit note" : "Credit note"}
        number={note.number}
        date={note.date}
        buyerLabel="Credited to"
        buyerName={note.customerName}
        buyerTaxId={note.customerTaxId}
        buyerRegistration={note.customerLegalRegistration}
      />

      <dl className="mb-4 flex gap-8 text-sm">
        <div>
          <dt className="text-muted-foreground text-xs">Against invoice</dt>
          <dd className="num text-foreground">{note.appliedTo ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">Reason</dt>
          <dd className="text-foreground">{note.reason}</dd>
        </div>
      </dl>

      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">What is being credited</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="text-muted-foreground py-1.5 text-left text-xs font-medium">
              Description
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {note.lines.map((line) => (
            <tr key={line.n} className="border-muted border-b">
              <td className="text-foreground py-1.5">{line.itemName}</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(line.lineNet)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* The net, the tax and the total, so the lines above visibly add up to what's credited. */}
      <div className="mt-4 ml-auto flex w-64 flex-col gap-1 text-sm">
        <p className="flex justify-between">
          <span className="text-muted-foreground">Net</span>
          <span className="num text-foreground">{formatAmount(note.totals.taxExclusive)}</span>
        </p>
        <p className="flex justify-between">
          <span className="text-muted-foreground">{say("VAT")}</span>
          <span className="num text-foreground">{formatAmount(note.totals.vat)}</span>
        </p>
        <p className="text-foreground border-border flex justify-between border-t pt-1 font-medium">
          <span>Total credited · NPR</span>
          <span className="num">{formatAmount(note.totals.taxInclusive)}</span>
        </p>
      </div>

      <PrintAmountInWords amount={note.totals.taxInclusive} />

      <PrintSignature byLabel="Prepared by" />

      <PrintFooter note={note.detail} />
    </article>
  );
}
